'use client'
import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { FileText, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Pagination } from '@/components/ui/pagination'

const PAGE_SIZE = 10

export function KnowledgeSourceSelectedFiles({ files, onRemove }: {
  files: Array<{ id: string; name: string; description?: string }>; onRemove?(id: string): void;
}) {
  const t = useTranslations('KnowledgeSources')
  const [page, setPage] = useState(1)
  const currentPage = Math.min(page, Math.max(1, Math.ceil(files.length / PAGE_SIZE)))
  const count = t('fileCount', { count: files.length })
  if (!files.length) return <p className="text-sm"><span key={count}>{count}</span></p>
  return <section aria-label={count} className="overflow-hidden rounded-lg border">
    <div className="border-b bg-muted/40 px-4 py-3 text-sm font-medium"><span key={count}>{count}</span></div>
    <ul className="max-h-80 divide-y overflow-y-auto">
      {files.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map(file => <li key={file.id} className="flex items-start gap-3 px-3 py-3 sm:px-4">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"><FileText aria-hidden="true" className="h-4 w-4" /></div>
        <div className="min-w-0 flex-1 space-y-1 py-1 text-sm [overflow-wrap:anywhere]">
          <span key={file.name}>{file.name}</span>
          {file.description && <p className="text-xs text-muted-foreground"><span key={file.description}>{file.description}</span></p>}
        </div>
        {onRemove && <Button variant="ghost" size="icon" type="button" className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
          aria-label={t('removeFile', { name: file.name })} title={t('removeFile', { name: file.name })} onClick={() => onRemove(file.id)}>
          <X aria-hidden="true" className="h-4 w-4" />
        </Button>}
      </li>)}
    </ul>
    {files.length > PAGE_SIZE && <Pagination page={currentPage} pageSize={PAGE_SIZE} total={files.length} onPageChange={setPage}
      pageNeighbours={0} className="gap-2 px-3 py-3 md:flex-col md:items-start sm:px-4 [&_button]:h-8 [&_button]:w-8" />}
  </section>
}
